/*
selectLocation.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import { useState, useEffect, useRef, lazy } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { COUNTRY_CODES } from '../../constants/countries'
import { ScrollWrapperRef } from '../../components/scrollWrapper'
import ScrollWrapperLoader from '../../components/scrollWrapperLoader'
import { useRedactionContext } from '../../providers/redactionProvider'
import { getDefaultStandardRedactionItems } from '../../modules/httpClients/presetClient'
import './selectLocation.scss'
import { errorTypes } from '../../constants/errorTypes'
import { useBaseContext } from '../../providers/baseProvider'
import { langCode } from '../../modules/util'
const Card = lazy(()=> import('../../components/Card')) 

export default function SelectLocation () {
  const navigate = useNavigate()
  const {setError} = useBaseContext();
  const { t } = useTranslation()
  const { draftRedactProfile, setDraftRedactProfile } = useRedactionContext()
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const selectCountryScrollRef = useRef<ScrollWrapperRef>(null)
  const selectedItemRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (draftRedactProfile?.country) {
      setSelectedCountry(draftRedactProfile.country)
    }
  }, [draftRedactProfile?.country])

  useEffect(() => {
    if (
      selectCountryScrollRef.current &&
      selectedItemRef.current &&
      selectedItemRef.current.id.toLocaleLowerCase() !==
        (`COUNTRY_` + selectedCountry).toLocaleLowerCase()
    ) {
      selectCountryScrollRef.current.scrollTo(
        -1 * selectedItemRef.current.offsetTop
      )
    }
  }, [])

  const handleCancel = () => {
    navigate(-1)
  }

  const handleOk = async () => {
    if (selectedCountry && draftRedactProfile) {
      try {
        const standardFields = await getDefaultStandardRedactionItems(
          selectedCountry,
          langCode,
          draftRedactProfile.deviceGuid
        )
        setDraftRedactProfile(prevState => {
          if (!prevState) return prevState
          return {
            ...prevState,
            country: selectedCountry,
            standardFields: standardFields
          }
        })
      } catch (error) {
        //handle error
        console.error('Failed to fetch default redaction items:', error)
        setError(errorTypes.apiError)
      }
    }
    navigate(-1)
  }

  return (
    <Card
      title={t('LOCATION')}
      onOk={handleOk}
      onCancel={handleCancel}
      XPadding='none'
    >
      <div className='country-list-table'>
        <ScrollWrapperLoader ref={selectCountryScrollRef} scrollId='select-country-scroller'>
          <div className='row mx-0'>
            <div className='col-12 px-0'>
              <ul className='xrx-list-group list-group-flush'>
                {COUNTRY_CODES.map(cc => (
                  <li
                    key={cc.countryCode}
                    id={`COUNTRY_${cc}`}
                    ref={ cc.countryCode.toLocaleLowerCase() === draftRedactProfile?.country.toLocaleLowerCase() ||
                      selectedCountry?.toLocaleLowerCase() ===
                      cc.countryCode.toLocaleLowerCase()
                        ? selectedItemRef
                        : null
                    }
                    className={`list-group-item divider list-group-item-action ${
                      selectedCountry === cc.countryCode ? 'active' : ''
                    }`}
                    onClick={() => setSelectedCountry(cc.countryCode)}
                  >
                    <div className='content-container'>
                      <div className='left-label pl-0'>
                        <span>
                          {t('LOCATIONS.' + cc.countryCode.toUpperCase())}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ScrollWrapperLoader>
      </div>
    </Card>
  )
}
